import { customBaseQuery } from '@/lib/baseQuery';
import { ApiResponse } from '@/shared/types';
import { createApi } from '@reduxjs/toolkit/query/react';
import { Lender } from '../routes/Recommendation/types/lender.type';
import { PurchaseSummaryDto } from '../routes/Recommendation/types/purchaseSummary.type';
import { GenerateMortgageTaxPdfDto } from '../routes/Recommendation/types/prelimPdfPayload.types';
import { LenderRecommendationType } from '../routes/Recommendation/types/recommendation.types';
import { LenderPricingResponseDto } from '../routes/Recommendation/types/lenderPricing.type';
import {
  CsaDocumentCode,
  CsaPackPreview,
  CsaPackRequest,
  CsaContent,
  CsaContentResponse,
  SavedCsaPackDocumentResult,
} from '../routes/RecommendationUpdated/types/csa-pack.types';
import type { CsaRef } from '../routes/RecommendationUpdated/utils/csa-ref.util';
type PropertyCalcRate = {
  lvr: number;
  interest: number;
};
type LenderInfo = {
  lender: string;
  rationale: string;
  maxBorrowingPower: string;
};
export type LenderDetails = {
  Product: string;
  'Max LVR': string;
  'Var. IR%': string;
  '1YR IR%': string;
  '2YR IR%': string;
  '3YR IR%': string;
  '4YR IR%': string;
  '5YR IR%': string;
  'One-time Fees': string;
  'Annual Fees': string;
  Features: string;
};
type LenderDescription = {
  [lenderName: string]: LenderDetails;
};
export const preliminaryApi = createApi({
  reducerPath: 'preliminaryApi',
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    fetchTopLenders: builder.query<
      ApiResponse<{
        topLenders: LenderInfo[];
        lenderDescription: LenderDescription;
      }>,
      void
    >({
      query: () => {
        return {
          url: `/v1/preliminary/top-lenders`,
        };
      },
    }),
    fetchLvr: builder.query<
      ApiResponse<{
        newPropertyRates: PropertyCalcRate[];
        existingPropertyRates: PropertyCalcRate[];
      }>,
      string
    >({
      query: (sheetId) => {
        return {
          url: `/v1/preliminary/property-calc-rates/${sheetId}`,
        };
      },
    }),
    sendPreliminaryAttachment: builder.mutation<
      ApiResponse<{ message: string }>,
      { email: string; name: string; lenders: Lender[] | [] }
    >({
      query: (body) => {
        return {
          url: '/v1/preliminary/send-assessment',
          method: 'POST',
          body,
        };
      },
    }),

    getFormFields: builder.mutation<
      ApiResponse<any>,
      { ocrText: any; outputFormat: Record<string, string> }
    >({
      query: (body) => {
        return {
          url: '/v1/preliminary/get-fields',
          method: 'POST',
          body,
        };
      },
    }),

    getSelectedLenders: builder.query<
      ApiResponse<{
        expatLenders: Record<string, string>[];
        selectedLenders: string[];
      }>,
      void
    >({
      query: () => {
        return {
          url: `/v1/lenders/selected-lenders`,
        };
      },
    }),

    getLenderRecommendationAllDetails: builder.query<
      ApiResponse<any>,
      { sheetId: string }
    >({
      query: ({ sheetId }) => ({
        url: `/google-sheet/lender-recommendation-all-details`,
        method: 'GET',
        params: { sheetId },
      }),
    }),

    postPreliminaryAssessmentSavedData: builder.mutation<
      ApiResponse<any>,
      {
        lenders: LenderRecommendationType[];
        sheet_id: string;
        oa_type: string;
        application_id: string;
      }
    >({
      query: (body) => ({
        url: `/v1/preliminary/save-preliminary-data`,
        method: 'POST',
        body,
      }),
    }),

    getPreliminaryAssessmentSavedData: builder.query<
      ApiResponse<any>,
      { applicationId: string; oaType: string }
    >({
      query: ({ applicationId, oaType }) => ({
        url: `/v1/preliminary/get-preliminary-data`,
        method: 'GET',
        params: { applicationId, oaType },
      }),
    }),

    generatePreliminaryPdf: builder.mutation<
      ApiResponse<any>,
      { lenders: Lender[] | [] }
    >({
      query: (body) => {
        return {
          url: '/v1/preliminary/pdf',
          method: 'POST',
          body,
        };
      },
    }),

    getPurchaseSummary: builder.query<
      ApiResponse<PurchaseSummaryDto>,
      { sheetId: string }
    >({
      query: ({ sheetId }) => ({
        url: `/v1/preliminary/purchase/summary/${sheetId}`,
        method: 'GET',
      }),
    }),
    getPricingLenders: builder.query<
      ApiResponse<LenderPricingResponseDto>,
      void
    >({
      query: () => ({
        url: `/v1/lenders/lenders/pricing`,
        method: 'GET',
      }),
    }),
    getLendersExpat: builder.query<ApiResponse<any>, void>({
      query: () => ({
        url: `/v1/preliminary/lenders-expat`,
        method: 'GET',
      }),
    }),

    getPreliminaryPreview: builder.mutation<
      string, // HTML string
      GenerateMortgageTaxPdfDto
    >({
      query: (body) => ({
        url: '/v1/preliminary/sla/pdf/preview',
        method: 'POST',
        body,
        // default responseHandler (JSON) is fine now
      }),
      transformResponse: (response: ApiResponse<string>) => response.data,
    }),

    getCsaContent: builder.query<CsaContentResponse, void>({
      query: () => ({ url: '/v1/preliminary/csa/content', method: 'GET' }),
      transformResponse: (response: ApiResponse<CsaContentResponse>) =>
        response.data,
    }),

    saveCsaContent: builder.mutation<CsaContent, CsaContent>({
      query: (content) => ({
        url: '/v1/preliminary/csa/content',
        method: 'PUT',
        body: { content },
      }),
      transformResponse: (response: ApiResponse<CsaContent>) => response.data,
    }),

    previewCsaPack: builder.mutation<
      CsaPackPreview,
      { body: CsaPackRequest; code?: CsaDocumentCode }
    >({
      query: ({ body, code }) => ({
        url: '/v1/preliminary/csa/pack/preview',
        method: 'POST',
        params: code ? { code } : undefined,
        body,
      }),
      transformResponse: (response: ApiResponse<CsaPackPreview>) =>
        response.data,
    }),

    // Renders and stores the combined CSA and all six documents; slow (seven PDFs), one row per document.
    saveCsaPack: builder.mutation<
      SavedCsaPackDocumentResult[],
      CsaPackRequest & { csaRef: CsaRef }
    >({
      query: (body) => ({
        url: '/v1/preliminary/csa/pack/save',
        method: 'POST',
        body,
      }),
      transformResponse: (
        response: ApiResponse<{ documents: SavedCsaPackDocumentResult[] }>,
      ) => response.data.documents,
    }),
  }),
});

export const {
  usePreviewCsaPackMutation,
  useSaveCsaPackMutation,
  useGetCsaContentQuery,
  useSaveCsaContentMutation,
  useFetchTopLendersQuery,
  useFetchLvrQuery,
  useSendPreliminaryAttachmentMutation,
  useGetFormFieldsMutation,
  useGetSelectedLendersQuery,
  useGetLenderRecommendationAllDetailsQuery,
  useGetPreliminaryAssessmentSavedDataQuery,
  usePostPreliminaryAssessmentSavedDataMutation,
  useGeneratePreliminaryPdfMutation,
  useGetPurchaseSummaryQuery,
  useGetPricingLendersQuery,
  useGetLendersExpatQuery,
  useGetPreliminaryPreviewMutation,
} = preliminaryApi;
